import type { GameRoomController } from "./game-room";

/**
 * Active room sessions, one per game (the backend seats a player in at most one
 * room per game). A session outlives the room page that started it, so the seat
 * heartbeat keeps running while the player is in chat or another app. It ends
 * only on an explicit leave, a lost seat / closed room, when another room of
 * the same game becomes active, or when the signed-in identity changes
 * (see session-teardown).
 */

type AnyController = GameRoomController<any, any>;

const active = new Map<string, AnyController>();
let onVisibility: (() => void) | null = null;
/**
 * Bumped by every identity teardown. A join (or a chat-launched room create)
 * that was issued under an older epoch resolved for the old identity and must
 * not become an active session under the new one.
 */
let identityEpoch = 0;

/** The current identity epoch; capture it before an async join/create, compare after. */
export function roomSessionEpoch(): number {
  return identityEpoch;
}

export function findRoomSession<C extends AnyController = AnyController>(game: string, roomId: string): C | null {
  const c = active.get(game);
  return c && c.roomId === roomId ? (c as C) : null;
}

/** Make `ctrl` its game's active session, retiring any other room's session of that game. */
export function activateRoomSession(ctrl: AnyController): void {
  const cur = active.get(ctrl.game);
  if (cur === ctrl) return;
  active.set(ctrl.game, ctrl);
  cur?.dispose();
  installVisibilityBeat();
}

export function deactivateRoomSession(ctrl: AnyController): void {
  if (active.get(ctrl.game) === ctrl) active.delete(ctrl.game);
}

export function activeRoomSessions(): AnyController[] {
  return [...active.values()];
}

/**
 * Sign-out or persona switch: every seat belongs to the old identity. Stop all
 * heartbeats and sockets (no leave is sent — the old credentials are gone or
 * belong to someone else); the server prunes the seats.
 */
export function endAllRoomSessions(): void {
  // Also covers joins still in flight: they aren't in `active` yet, and bail on resolve.
  identityEpoch++;
  for (const c of [...active.values()]) c.end();
  active.clear();
}

/** Test seam. */
export function resetRoomSessions(): void {
  for (const c of [...active.values()]) c.dispose();
  active.clear();
  if (onVisibility && typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
  onVisibility = null;
}

/**
 * Mobile browsers freeze timers while the app is backgrounded; beat as soon as
 * the page is visible again rather than waiting out the interval.
 */
function installVisibilityBeat(): void {
  if (onVisibility || typeof document === "undefined" || typeof document.addEventListener !== "function") return;
  onVisibility = () => {
    if (document.visibilityState !== "visible") return;
    for (const c of active.values()) c.beatNow();
  };
  document.addEventListener("visibilitychange", onVisibility);
}
